import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { YarnBall } from "@/components/yarn/YarnBall";
import { useAuth } from "@/context/AuthContext";
import { api, errorText } from "@/lib/api";

const SLOT = "h-14 w-11 sm:w-12 rounded-xl first:rounded-xl last:rounded-xl border-2 first:border-2 bg-white text-2xl font-display border-[var(--yarn-stitch)]";

export default function VerifyPage() {
  const [params] = useSearchParams();
  const username = params.get("u") || "";
  const { signIn } = useAuth();
  const [hint, setHint] = useState(params.get("hint") || "your email");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(45);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!username) return <Navigate to="/" replace />;

  const verify = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify", { username, code: value });
      toast.success("Email confirmed. Welcome to Yarn!");
      signIn(data.token, data.user);
    } catch (err) {
      toast.error(errorText(err));
      setCode("");
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend", { username });
      setHint(data.email_hint);
      setCooldown(45);
      if (data.email_sent) toast.success("A fresh code is on its way.");
      else toast.error("We couldn't send the email. Try again in a moment.");
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  return (
    <div className="flex-1 grid place-items-center px-5 py-10">
      <div className="patch patch-stitched w-full max-w-md p-7 sm:p-9 rise-in text-center" data-testid="verify-page">
        <YarnBall size={56} className="mx-auto yarn-bob" />
        <h1 className="font-display text-3xl font-semibold mt-5">Check your email</h1>
        <p className="mt-2 text-sm text-[var(--yarn-muted)]">
          We sent a 6-digit code to <span className="font-semibold text-[var(--yarn-ink)]" data-testid="verify-email-hint">{hint}</span> for <b>{username}</b>.
        </p>
        <div className="mt-7 flex justify-center">
          <InputOTP maxLength={6} value={code} disabled={busy} inputMode="numeric" autoFocus data-testid="verify-code-input"
            onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }}>
            <InputOTPGroup className="gap-2">
              {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className={SLOT} />)}
            </InputOTPGroup>
          </InputOTP>
        </div>
        <button className="btn-yarn w-full h-14 text-lg mt-7" disabled={busy || code.length !== 6}
          onClick={() => verify(code)} data-testid="verify-submit-button">
          {busy ? "Checking..." : "Confirm"}
        </button>
        <button className="btn-soft w-full h-12 mt-3" disabled={cooldown > 0} onClick={resend} data-testid="verify-resend-button">
          {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
        </button>
        <Link to="/" className="inline-block mt-5 text-sm underline text-[var(--yarn-muted)]" data-testid="verify-back-link">
          Back to sign up
        </Link>
      </div>
    </div>
  );
}
